interface Props {
  nombre: string;
  registrosError: number;
  registrosOk: number;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Modal de confirmación para separar inconsistencias en un padrón nuevo.
 *
 * NO es una acción destructiva (los registros se mueven, no se pierden) pero
 * mueve data entre padrones y dispara un recompute completo, por eso pedimos
 * confirmación explícita.
 *
 * El backend mueve por LEGAJO entero (titular + familiares) para preservar la
 * integridad del grupo, así que la cantidad real de filas movidas puede ser
 * mayor a `registrosError` cuando un legajo tiene errores en algunos
 * familiares pero el titular y/o otros familiares están OK.
 */
export function SepararInconsistenciasModal({
  nombre,
  registrosError,
  registrosOk,
  submitting,
  error,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <div className="modalBackdrop" onClick={onCancel}>
      <div className="modalCard" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <header style={{ marginBottom: 16 }}>
          <h2 className="heading-3" style={{ color: 'var(--color-primary-darker)' }}>
            Separar inconsistencias en un padrón nuevo
          </h2>
          <p className="text-element-mid" style={{ marginTop: 6, fontSize: 14 }}>
            Vamos a partir <strong>{nombre}</strong> en dos padrones:
          </p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              background: 'var(--color-surface-alt)',
              border: '1px solid var(--color-outline)',
              borderTop: '3px solid #047857',
            }}
          >
            <span
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-element-mid)',
                fontWeight: 600,
              }}
            >
              Padrón original
            </span>
            <p style={{ marginTop: 6, fontSize: 13, color: 'var(--color-element-mid)' }}>
              Queda solo con los legajos sin errores
              <strong style={{ color: '#047857' }}> (~{registrosOk.toLocaleString('es-AR')} registros OK)</strong>.
            </p>
          </div>
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              background: 'var(--color-surface-alt)',
              border: '1px solid var(--color-outline)',
              borderTop: '3px solid #B45309',
            }}
          >
            <span
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--color-element-mid)',
                fontWeight: 600,
              }}
            >
              Padrón nuevo
            </span>
            <p style={{ marginTop: 6, fontSize: 13, color: 'var(--color-element-mid)' }}>
              Recibe los legajos con al menos 1 error
              <strong style={{ color: '#B45309' }}> (~{registrosError.toLocaleString('es-AR')} registros con error</strong> + sus familiares OK).
            </p>
          </div>
        </div>

        <ul
          className="text-element-mid"
          style={{ marginLeft: 20, fontSize: 13, lineHeight: 1.6 }}
        >
          <li>Los legajos se mueven <strong>enteros</strong> (titular + familiares) para preservar la integridad del grupo.</li>
          <li>Ambos padrones quedan en <strong>borrador</strong> — podés seguir corrigiendo cada uno por separado.</li>
          <li>El padrón nuevo se llamará <code>Inconsistencias — {nombre}</code>.</li>
          <li>Esta operación puede tardar <strong>hasta un minuto</strong> en padrones grandes.</li>
        </ul>

        {error && (
          <p className="status status-error" style={{ marginTop: 14, display: 'block' }}>
            {error}
          </p>
        )}

        <div className="rowBetween" style={{ marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={submitting}
            style={{ minWidth: 180 }}
          >
            <span className="btn-font-style">
              {submitting ? 'Separando…' : 'Separar inconsistencias'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
